import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { 
  Watch, 
  Zap, 
  Battery, 
  Heart, 
  Moon, 
  Bell, 
  ArrowRight, 
  CheckCircle2, 
  Clock, 
  Loader2, 
  Lock 
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useToolTheme } from '@/contexts/ThemeContext';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { QRCodeDownload } from '@/components/projector-watch/QRCodeDownload';

const SUPER_ADMIN_EMAIL = 'solutions.oko@gmail.com';

export default function ProjectorWatchPage() {
  const { toolColors } = useToolTheme();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [betaAccess, setBetaAccess] = useState<{
    status: 'pending' | 'approved' | 'testing' | null;
    inviteCode?: string;
  }>({ status: null });

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    const checkBetaAccess = async () => {
      try {
        const { data, error } = await supabase
          .from('beta_access')
          .select('status, invite_code')
          .eq('user_id', currentUser.id)
          .eq('program', 'projector-watch')
          .maybeSingle();

        if (error) throw error;
        setBetaAccess(data || { status: null });
      } catch (error) {
        console.error('Error checking beta access:', error);
        toast({
          variant: "destructive",
          title: "Erreur",
          description: "Impossible de vérifier l'accès bêta.",
        });
      } finally {
        setLoading(false);
      }
    };

    checkBetaAccess();
  }, [currentUser, toast]);

  const handleAccess = async () => {
    if (!currentUser) {
      toast({
        title: "Connexion requise",
        description: "Veuillez vous connecter pour accéder à Projector Watch.",
      });
      navigate('/login');
      return;
    }

    if (betaAccess.status === 'approved' || betaAccess.status === 'testing') {
      navigate('/projector-watch/dashboard');
      return;
    }

    try {
      setLoading(true);
      
      if (betaAccess.status === 'pending') {
        toast({
          title: "Demande en cours",
          description: "Votre demande d'accès est en cours d'examen.",
        });
        return;
      }

      const { error } = await supabase
        .from('beta_access')
        .insert({
          user_id: currentUser.id,
          program: 'projector-watch',
          status: 'pending'
        });

      if (error) throw error;

      setBetaAccess({ status: 'pending' });
      toast({
        title: "Demande envoyée !",
        description: "Votre demande d'accès au programme bêta a été enregistrée.",
      });
    } catch (error) {
      console.error('Error requesting beta access:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible d'envoyer la demande d'accès.",
      });
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      title: "Suivi énergétique en temps réel",
      description: "Recevez des alertes lorsque votre énergie fluctue et adaptez vos activités en conséquence.",
      icon: <Zap className="w-6 h-6" style={{ color: toolColors.primary }} />
    },
    {
      title: "Analyse du sommeil",
      description: "Suivez vos cycles de sommeil et recevez des recommandations pour optimiser votre repos.",
      icon: <Moon className="w-6 h-6" style={{ color: toolColors.primary }} />
    },
    {
      title: "Monitoring cardiaque",
      description: "Surveillez votre fréquence cardiaque et identifiez les moments de stress ou de fatigue.",
      icon: <Heart className="w-6 h-6" style={{ color: toolColors.primary }} />
    }
  ];

  return (
    <motion.div 
      className="min-h-screen tool-background projector-watch-background"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-6xl mx-auto space-y-8 px-4 py-20">
        {/* Hero Section */}
        <div className="text-center space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-block p-4 bg-white rounded-2xl shadow-xl mb-4"
          >
            <Watch className="w-12 h-12" style={{ color: toolColors.primary }} />
          </motion.div>
          <motion.h1 
            className="text-4xl font-bold hero-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            style={{ color: toolColors.primary }}
          >
            Projector Watch
          </motion.h1>
          <motion.p 
            className="text-xl max-w-3xl mx-auto hero-text"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            L'application de suivi énergétique pour montre connectée qui vous aide à optimiser votre énergie tout au long de la journée
          </motion.p>
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Button
              onClick={handleAccess}
              className="gap-2"
              style={{ 
                backgroundColor: toolColors.primary,
                color: 'white'
              }}
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <ArrowRight className="w-4 h-4" />
                  {betaAccess.status === 'approved' || betaAccess.status === 'testing'
                    ? "Accéder au dashboard"
                    : betaAccess.status === 'pending'
                    ? "Demande en cours"
                    : "Demander l'accès"}
                </>
              )}
            </Button>
            <Button 
              onClick={() => navigate('/projector-watch/test-antigravite')}
              variant="outline"
              className="gap-2"
              style={{ 
                borderColor: toolColors.primary,
                color: toolColors.primary
              }}
            >
              <Zap className="w-4 h-4" />
              Test Antigravité
            </Button>
          </motion.div>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
            >
              <Card className="hover:shadow-lg transition-all bg-white/90 backdrop-blur-sm">
                <CardContent className="p-6">
                  <div className="flex flex-col h-full">
                    <div className="p-3 rounded-lg self-start mb-4" style={{ backgroundColor: `${toolColors.primary}15` }}>
                      {feature.icon}
                    </div>
                    <h3 className="text-lg font-semibold mb-2 text-enhanced" style={{ color: toolColors.primary }}>{feature.title}</h3>
                    <p className="text-gray-800 flex-grow text-enhanced">{feature.description}</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* QR Code Section */}
        <div className="mt-12">
          <QRCodeDownload />
        </div>

        {/* Beta Access Info */}
        {betaAccess.status === 'approved' && betaAccess.inviteCode && (
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
                <h2 className="text-xl font-bold text-green-800">Accès approuvé !</h2>
              </div>
              <p className="text-green-700 mb-4">
                Votre code d'invitation : <code className="bg-white px-2 py-1 rounded">{betaAccess.inviteCode}</code>
              </p>
              <Button onClick={() => navigate('/projector-watch/dashboard')} className="gap-2">
                <ArrowRight className="w-4 h-4" />
                Accéder au dashboard
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </motion.div>
  );
}